'use client';

import {
  ProductBundleCard,
  ProductBundleCardBody,
  ProductBundleCardCheck,
  ProductBundleCardCopy,
  ProductBundleCardHeader,
  ProductBundleCardImage,
  ProductBundleCardLabel,
  ProductBundleCardMedia,
  ProductBundleCardName,
  ProductBundleCardPrice,
} from '@vs-shop/commerce-ui/productBundle';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';

import { getDefaultCurrency } from '../../../../config/currency';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import Link from '@vs-shop/image-link/Link';
import { IntlMoney } from '../../../server/intl/Intl';

import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  imagePath: string | null;
  routerUrl: string;
  productName: string;
  label: string | null;
  price: number;
  isSelected: boolean;
  disabled?: boolean;
  onToggleAction: () => void;
};

/**
 * One line of rcprofi's bundle: the shared card parts fed this tenant's image
 * path, router url, words and money.
 *
 * The image filter is resolved here — the part takes a final, renderable URL —
 * and a line with no picture composes the placeholder instead of the image.
 */
export default function ProductDiscountSetItem({
  imagePath,
  routerUrl,
  productName,
  label,
  price,
  isSelected,
  disabled = false,
  onToggleAction,
}: Props) {
  const currency = getDefaultCurrency();

  const resolvedImagePath = imagePath
    ? resolveImageUrl(imagePath, ImageFilter.product_set_item)
    : null;

  return (
    <ProductBundleCard>
      <ProductBundleCardBody selected={isSelected} disabled={disabled}>
        {/* CHECKBOX */}
        <ProductBundleCardCheck
          checked={isSelected}
          disabled={disabled}
          onCheckedChange={() => {
            if (!disabled) {
              onToggleAction();
            }
          }}
        />

        {/* IMAGE */}
        <ProductBundleCardMedia render={<Link href={routerUrl} />}>
          {resolvedImagePath !== null ? (
            <ProductBundleCardImage
              src={resolvedImagePath}
              alt={productName}
              width={260}
              height={195}
              preload
            />
          ) : (
            <ImageMissingPlaceholder />
          )}
        </ProductBundleCardMedia>

        {/* LABEL, NAME, PRICE */}
        <ProductBundleCardCopy>
          <ProductBundleCardHeader>
            {label ? <ProductBundleCardLabel>{label}</ProductBundleCardLabel> : null}
            <ProductBundleCardName render={<Link href={routerUrl} />}>
              {productName}
            </ProductBundleCardName>
          </ProductBundleCardHeader>
          <ProductBundleCardPrice>
            <IntlMoney value={{ amount: price, currency }} />
          </ProductBundleCardPrice>
        </ProductBundleCardCopy>
      </ProductBundleCardBody>
    </ProductBundleCard>
  );
}
